/**
 * 1.3.1 Administration → Media portal → Branding → Email texts (Q1): each supplier email's subject and body
 * (PORTAL_EMAIL_KINDS), its placeholders as chips, a live preview (POST …/email-texts/:kind/preview, or
 * fillPlaceholders with PORTAL_EMAIL_SAMPLE_VARS while typing), Save, and Reset to default. Unknown placeholders are
 * shown before saving (unknownPlaceholders) and refused by the API (422 EMAIL_TEXT_PLACEHOLDER). Rendered by
 * PortalBranding (Settings.tsx). Q1's file.
 */
import { useEffect, useRef, useState } from 'react';
import { Mail, RotateCcw, Save } from 'lucide-react';
import {
  PORTAL_EMAIL_SAMPLE_VARS, fillPlaceholders, unknownPlaceholders, type PortalEmailKind, type PortalEmailPreviewDTO, type PortalEmailTextDTO,
} from '@prism/contracts';
import { ApiError, api, errorDetail } from '../../../api/client';
import { usePortalAdminRefresh, usePortalEmailTexts, usePortalSettings } from '../../../api/hooks/portalAdmin';
import { toast } from '../../../components/Toasts';

const KIND_LABEL: Record<PortalEmailKind, string> = {
  portal_invitation: 'Invitation',
  portal_password_reset: 'Password reset',
  portal_two_step_reset: 'Two-step sign-in reset',
  portal_assignment_new: 'New delivery assigned',
  portal_assignment_changed: 'Delivery changed',
  portal_assignment_cancelled: 'Delivery cancelled',
  portal_item_rejected: 'Item rejected',
  portal_submission_accepted: 'Delivery accepted',
  portal_submission_rejected: 'Delivery rejected',
  portal_staff_comment: 'Comment from PRISM',
  portal_submission_received: 'Upload received',
  portal_qc_result: 'Quality check result',
  portal_reminder: 'Reminder before the due date',
};

export function PortalEmailTexts() {
  const texts = usePortalEmailTexts();
  const [kind, setKind] = useState<PortalEmailKind>('portal_invitation');
  const items = texts.data?.items ?? [];
  const current = items.find((i) => i.kind === kind);
  return (
    <div className="pb-extra" data-testid="portal-email-texts">
      <h4><Mail size={15} /> Email texts</h4>
      <p className="muted portal-lede">What each email to suppliers says. Change the subject or the text, using the placeholders listed for that email; Reset to default brings back PRISM’s own text.</p>
      <div className="pe-layout">
        <ul className="pe-kinds" role="listbox" aria-label="Supplier emails">
          {items.map((i) => (
            <li key={i.kind}>
              <button type="button" role="option" aria-selected={i.kind === kind} className={`pe-kind ${i.kind === kind ? 'on' : ''}`} onClick={() => setKind(i.kind)} data-testid={`portal-email-kind-${i.kind}`}>
                <span>{KIND_LABEL[i.kind] ?? i.kind}</span>
                {i.overridden && <span className="pill accent pe-changed">Changed</span>}
              </button>
            </li>
          ))}
        </ul>
        {current ? <EmailTextEditor key={`${current.kind}:${current.overridden}:${current.subject}`} text={current} /> : <p className="muted">{texts.isLoading ? 'Loading…' : 'No emails.'}</p>}
      </div>
    </div>
  );
}

function EmailTextEditor({ text }: { text: PortalEmailTextDTO }) {
  const refresh = usePortalAdminRefresh();
  const portalName = usePortalSettings().data?.branding.portalName || 'Media portal';
  const [subject, setSubject] = useState(text.subject);
  const [body, setBody] = useState(text.body);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const focused = useRef<'subject' | 'body'>('body');
  const subjectRef = useRef<HTMLInputElement>(null);
  const unknown = [...new Set([...unknownPlaceholders(text.kind, subject), ...unknownPlaceholders(text.kind, body)])];
  const dirty = subject !== text.subject || body !== text.body;
  const vars = { ...PORTAL_EMAIL_SAMPLE_VARS, portalName };

  // The branded HTML as the supplier gets it, from the API a moment after typing stops.
  useEffect(() => {
    const h = window.setTimeout(() => {
      api<PortalEmailPreviewDTO>(`/api/v1/admin/portal/email-texts/${text.kind}/preview`, { method: 'POST', json: { subject: subject || undefined, body: body || undefined } })
        .then((p) => setHtml(p.html)).catch(() => setHtml(null));
    }, 400);
    return () => window.clearTimeout(h);
  }, [text.kind, subject, body]);

  const insert = (name: string) => {
    const token = `{${name}}`;
    const el = focused.current === 'subject' ? subjectRef.current : bodyRef.current;
    const value = focused.current === 'subject' ? subject : body;
    const set = focused.current === 'subject' ? setSubject : setBody;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    set(value.slice(0, start) + token + value.slice(end));
    window.setTimeout(() => { el?.focus(); el?.setSelectionRange(start + token.length, start + token.length); }, 0);
  };

  async function save() {
    setBusy(true);
    setRefused(null);
    try {
      await api(`/api/v1/admin/portal/email-texts/${text.kind}`, { method: 'PUT', json: { subject, body } });
      await refresh();
      toast(`${KIND_LABEL[text.kind]}: text saved`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'EMAIL_TEXT_PLACEHOLDER') setRefused(err.message);
      else toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    if (!window.confirm(`Reset “${KIND_LABEL[text.kind]}” to PRISM’s default text?`)) return;
    setBusy(true);
    try {
      await api(`/api/v1/admin/portal/email-texts/${text.kind}`, { method: 'DELETE' });
      await refresh();
      toast(`${KIND_LABEL[text.kind]}: default text back`);
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pe-editor" data-testid="portal-email-editor" data-kind={text.kind}>
      <div className="field">
        <label htmlFor="pe-subject">Subject</label>
        <input id="pe-subject" ref={subjectRef} className="input" maxLength={300} value={subject} onFocus={() => { focused.current = 'subject'; }} onChange={(e) => setSubject(e.target.value)} data-testid="portal-email-subject" />
      </div>
      <div className="field">
        <label htmlFor="pe-body">Text</label>
        <textarea id="pe-body" ref={bodyRef} className="textarea pe-body" maxLength={20000} value={body} onFocus={() => { focused.current = 'body'; }} onChange={(e) => setBody(e.target.value)} data-testid="portal-email-body" />
      </div>
      <div className="pe-chips" aria-label="Placeholders this email can use">
        <span className="faint">Placeholders:</span>
        {text.placeholders.map((p) => (
          <button key={p} type="button" className="chip pe-chip" onClick={() => insert(p)} title={`Insert {${p}}, e.g. “${vars[p as keyof typeof vars] ?? ''}”`}>{`{${p}}`}</button>
        ))}
      </div>
      {unknown.length > 0 && (
        <p className="portal-bad pe-unknown" role="alert" data-testid="portal-email-unknown">
          This email cannot use {unknown.map((u) => `{${u}}`).join(', ')}: saving is refused until {unknown.length > 1 ? 'they are' : 'it is'} removed.
        </p>
      )}
      {refused && <p className="portal-bad pe-unknown" role="alert">{refused}</p>}
      <div className="pe-preview" data-testid="portal-email-preview">
        <div className="pe-preview-head"><span className="faint">Preview with example values</span></div>
        <div className="pe-preview-subject"><span className="faint">Subject:</span> <strong data-testid="portal-email-preview-subject">{fillPlaceholders(subject, vars)}</strong></div>
        {html
          ? <iframe className="pe-preview-frame" title="The email as suppliers see it" sandbox="" srcDoc={`<!doctype html><meta charset="utf-8"><body style="margin:12px;background:#fff">${html}</body>`} />
          : <pre className="pe-preview-text" data-testid="portal-email-preview-text">{fillPlaceholders(body, vars)}</pre>}
      </div>
      <div className="portal-save-row">
        <button className="btn primary" disabled={!dirty || busy || unknown.length > 0 || !subject.trim() || !body.trim()} onClick={() => void save()} data-testid="portal-email-save"><Save size={14} /> Save text</button>
        <button className="btn ghost" disabled={!text.overridden || busy} onClick={() => void reset()} data-testid="portal-email-reset"><RotateCcw size={14} /> Reset to default</button>
        {dirty && <button className="btn ghost" disabled={busy} onClick={() => { setSubject(text.subject); setBody(text.body); }}>Discard changes</button>}
      </div>
    </div>
  );
}
